import styled from "@emotion/styled";
import { ChannelPlaylistsDisplayType, ChannelSectionProps, EventPlatformEntry } from "@mediaspace/shared/types";
import { baseUrl, getDataFromKms, translate, useMediaQuery } from "@mediaspace/shared/utils";
import { SinglePlaylist, SinglePlaylistSkeleton } from "@mediaspace/shared/ui";
import { Config, useConfig } from "@mediaspace/shared/data-kms-config";
import { systemWidth, useTheme } from "@mediaspace/shared/styled";
import { useState } from "react";
import { Button } from "@kaltura/ds-react-components";

type EventPlatformPlaylist = {
    entries: EventPlatformEntry[];
    id: string;
    name: string;
};

/**
 * expected server response when loading more playlists
 */
type PlaylistsResponse = {
    /**
     * error description in case of error
     */
    error? : string;

    /**
     * playlists data
     */
    playlists: EventPlatformPlaylist[];

    /**
     * are there more playlists to load
     */
    morePlaylists: boolean;
}


export interface PlaylistCarouselsProps extends ChannelSectionProps<EventPlatformPlaylist[]> {
    hasMorePlaylists: boolean;
}

const StyledCarousel = styled("div")(({ theme }) => ({
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down("md")]: {
        marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down("sm")]: {
        marginBottom: theme.spacing(2),
    },
}));

const ButtonContainer = styled("div")(({ theme }) => ({
    "&": systemWidth({ theme }),
    display: "flex",
    justifyContent: "center",
}));

/**
 * Channel page playlists section
 */
export function PlaylistCarousels(props: PlaylistCarouselsProps) {
    const {
        content,
        categoryId = undefined,
        hasMorePlaylists: hasMorePlaylistsProp
    } = props;

    const config: Config = useConfig();
    const channelPlaylistsDisplay = config?.channelPlaylists?.playlistDisplay;

    const theme = useTheme();
    const isSmScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

    /**
     * playlists to show
     */
    const [playlists, setPlaylists] = useState<EventPlatformPlaylist[]>(content);

    /**
     * do we have more playlists to load from server?
     */
    const [hasMorePlaylists, setHasMorePlaylists] = useState(hasMorePlaylistsProp);

    /**
     * number of chunks ("pages") already loaded.
     * the first chunk comes with initial page props
     */
    const [chunksLoaded, setChunksLoaded] = useState(1);

    /**
     * is app currently loading more playlists
     */
    const [loading, setLoading] = useState(false);

    const getPlaylistUrl = (id: string) => {
        return channelPlaylistsDisplay === ChannelPlaylistsDisplayType.player
            ? `${baseUrl}/playlist/dedicated/${categoryId ? categoryId + '/' : ''}${id}`
            : `${baseUrl}/playlist/${id}`
    };


    const onMorePlaylistsLoaded = (data: PlaylistsResponse) => {
        if (data.error) {
            onMorePlaylistsLoadError(data.error);
            return;
        }
        setPlaylists(oldVal => [...oldVal, ...data.playlists]);
        setHasMorePlaylists(data.morePlaylists);
        setChunksLoaded(oldVal => oldVal + 1);
        setLoading(false);
    }

    const onMorePlaylistsLoadError = (error: string) => {
        // stop loading, don't allow retry
        console.log("error getting more playlists: " + error);
        setLoading(false);
        setHasMorePlaylists(false);
    }

    const loadMore = () => {
        if (loading) return;

        setLoading(true);
        // get the next chunk of playlists
        const query = {
            page: chunksLoaded + 1,
            categoryId: categoryId,
        }
        getDataFromKms(baseUrl + "/channelthemingui/index/get-playlists", query, onMorePlaylistsLoaded, false, onMorePlaylistsLoadError)
    }

    return (
        <div className={"kms-ds-channel-page-section-playlists"}>
            {playlists.map(({ entries, id, name }) => {
                if (entries.length === 0) {
                    return null;
                }
                return (
                    <StyledCarousel key={id}>
                        <SinglePlaylist
                            entries={entries}
                            playlistId={id}
                            categoryId={categoryId}
                            translatedTitle={name}
                            titleLink={getPlaylistUrl(id)}
                            translatedLinkString={translate("See All")}
                            fullScreenWidth={isSmScreen}
                        />
                    </StyledCarousel>
                );
            })}
            {loading && (<SinglePlaylistSkeleton fullScreenWidth={isSmScreen} />)}
            {hasMorePlaylists && !loading && (
                <ButtonContainer>
                    <Button color={"secondary"} onClick={loadMore} >{translate("Load more playlists")}</Button>
                </ButtonContainer>
            )}
        </div>
    );
}

export default PlaylistCarousels;
